import React, { useState } from 'react';
import { Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { RiskFactorScores, FACTOR_CONFIG, FACTOR_SEVERITY_COLORS, FactorSeverity, getRiskBand } from './RiskAreasWidget';
import { RISK_FACTORS, calculateRiskScore, mapRiskFactorScores, severityToNumeric } from '../utils/riskCalculation';
interface RiskScoreBreakdownProps {
  scores: RiskFactorScores;
  riskScore?: number;
  onTuneWeights?: () => void;
}
const RESEARCH_FACTOR_LABELS: Record<string, {
  label: string;
  icon: string;
}> = {
  visual_hazards: {
    label: 'Visual Hazards',
    icon: '👁️'
  },
  human_factors: {
    label: 'Human Factors',
    icon: '🧠'
  },
  operational_friction: {
    label: 'Op. Friction',
    icon: '⚙️'
  },
  documentation_quality: {
    label: 'Doc Quality',
    icon: '📝'
  }
};
export function RiskScoreBreakdown({
  scores,
  riskScore,
  onTuneWeights
}: RiskScoreBreakdownProps) {
  const [expandedFactor, setExpandedFactor] = useState<string | null>(null);
  const calculation = calculateRiskScore(scores);
  const mapped = mapRiskFactorScores(scores);
  const getSeverityColor = (severity: FactorSeverity) => {
    switch (severity) {
      case 'high':
        return {
          bar: 'bg-red-500',
          text: 'text-red-700',
          bg: 'bg-red-100'
        };
      case 'medium':
        return {
          bar: 'bg-amber-400',
          text: 'text-amber-700',
          bg: 'bg-amber-100'
        };
      case 'low':
        return {
          bar: 'bg-emerald-400',
          text: 'text-emerald-700',
          bg: 'bg-emerald-100'
        };
      default:
        return {
          bar: 'bg-gray-200',
          text: 'text-gray-400',
          bg: 'bg-gray-50'
        };
    }
  };
  const getBarHeight = (severity: FactorSeverity) => {
    switch (severity) {
      case 'high':
        return 'h-8';
      case 'medium':
        return 'h-5';
      case 'low':
        return 'h-3';
      default:
        return 'h-1';
    }
  };
  const factorEntries = Object.entries(RISK_FACTORS).map(([key, factor]) => {
    const severity = mapped[key as keyof typeof mapped];
    const points = calculation.breakdown[key as keyof typeof calculation.breakdown];
    return {
      key,
      factor,
      severity,
      points
    };
  });
  return <div data-id="element-3723" data-mp="315drcbf79gsu">
      {/* Contributing Factors - Vertical Bars */}
      <div className="mb-4" data-id="element-3724" data-mp="3f5ojriw5r3g4">
        <div className="flex items-center gap-2 mb-3" data-id="element-3725" data-mp="3f4vaaa5tdzua">
          <span className="text-lg" data-id="element-3726" data-mp="3b4baeytuwi4g">📊</span>
          <h3 className="font-bold text-gray-900" data-id="element-3727" data-mp="3b3jroolcyevl">Contributing Factors</h3>
          {riskScore !== undefined && <span className="text-xs text-gray-400 ml-auto" data-id="element-3728" data-mp="3ov7zifokqh6r">
              Risk Band: {getRiskBand(riskScore).label} (
              {getRiskBand(riskScore).range})
            </span>}
        </div>

        <div className="grid grid-cols-4 gap-3" data-id="element-3729" data-mp="2orpqo794810">
          {factorEntries.map(({
          key,
          factor,
          severity,
          points
        }) => {
          const colors = getSeverityColor(severity);
          const barH = getBarHeight(severity);
          const isExpanded = expandedFactor === key;
          const info = RESEARCH_FACTOR_LABELS[key];
          return <button key={key} onClick={() => setExpandedFactor(isExpanded ? null : key)} className={`flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all cursor-pointer ${isExpanded ? 'bg-gray-50 ring-1 ring-indigo-200' : 'hover:bg-gray-50'}`} data-id="element-3730" data-mp="7du2xihvjrde">
                <div className="w-full h-10 flex items-end justify-center" data-id="element-3731" data-mp="2er80qvlz1j5">
                  <div className={`w-full max-w-[32px] rounded-t ${barH} ${colors.bar} transition-all`} data-id="element-3732" data-mp="3ksqeqq2dvup8" />
                </div>
                <span className="text-[10px] font-semibold text-gray-600 text-center leading-tight" data-id="element-3733" data-mp="3atrf1wzlj9ed">
                  {info?.label || factor.name}
                </span>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`} data-id="element-3734" data-mp="3fs2o45i2hfvj">
                  {severity === 'none' ? '—' : severity}
                </span>
                <span className="text-[9px] font-bold text-gray-500" data-id="element-3735" data-mp="d8kkbhuritni">
                  {points} pts
                </span>
              </button>;
        })}
        </div>

        {/* Expanded Study Info */}
        {expandedFactor && RISK_FACTORS[expandedFactor] && <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm" data-id="element-3736" data-mp="381tg34s6cw3">
            <div className="flex items-center justify-between mb-2" data-id="element-3737" data-mp="3vga0vdaqq5bf">
              <span className="font-bold text-gray-900" data-id="element-3738" data-mp="3qfvstv952vde">
                {RISK_FACTORS[expandedFactor].name}
                <span className="font-normal text-gray-400 ml-1" data-id="element-3739" data-mp="3lhkjdlmramvp">
                  ({RISK_FACTORS[expandedFactor].defaultWeight * 100}% weight)
                </span>
              </span>
              <button onClick={() => setExpandedFactor(null)} className="text-gray-400 hover:text-gray-600" data-id="element-3740" data-mp="1jpxxfyhq1i63">
                <ChevronUp size={14} data-id="element-3741" data-mp="1w6yyqlqvh2zc" />
              </button>
            </div>
            <div className="space-y-1.5 text-xs text-gray-600" data-id="element-3742" data-mp="1w4vzrc7qrzih">
              <p data-id="element-3743" data-mp="20z9xvmdsgkqu">
                <span className="font-semibold text-gray-700" data-id="element-3744" data-mp="20yqpoxdxxaq0">Study:</span>{' '}
                {RISK_FACTORS[expandedFactor].study.name} (
                {RISK_FACTORS[expandedFactor].study.dateRange})
              </p>
              <p data-id="element-3745" data-mp="18bnt4astra2e">
                <span className="font-semibold text-gray-700" data-id="element-3746" data-mp="1d84nhmqmaknx">Logic:</span>{' '}
                {RISK_FACTORS[expandedFactor].study.logic}
              </p>
              <p data-id="element-3747" data-mp="1i6631xou41un">
                <span className="font-semibold text-gray-700" data-id="element-3748" data-mp="1i9twkwua4o9d">Impact:</span>{' '}
                {RISK_FACTORS[expandedFactor].study.impact}
              </p>
            </div>
          </div>}
      </div>
    </div>;
}